import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { endSetPassword } from "../lib/setpw";
import { homePath } from "../lib/pwa";

const MIN = 10;

/**
 * Shown for: invite links, password-reset links, and users who signed in with a
 * temporary password set by Skyward (must_change_password).
 */
export default function SetPassword({ session, reason }: { session: Session | null; reason: "invite" | "reset" | "temporary" }) {
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Expired / already-used links come back with an error in the URL hash
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const linkError = hash.get("error_description");

  if (!session) {
    return (
      <div className="login">
        <div className="login-card">
          <img src="/logo.png" alt="Tracking Fiji" className="login-logo" />
          <h1>Link not valid</h1>
          <p className="muted">
            {linkError ? `${linkError.replace(/\+/g, " ")}. ` : ""}
            This link has expired or was already used. Reset links are now replaced by a code — use “Forgot password” to get one.
          </p>
          <button className="btn-primary" onClick={() => nav("/reset", { replace: true })}>Reset with a code</button>
          <button className="btn-ghost" onClick={() => nav("/login", { replace: true })}>Go to sign in</button>
        </div>
      </div>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (pw.length < MIN) return setError(`Use at least ${MIN} characters.`);
    if (pw !== pw2) return setError("The two passwords don't match.");
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: pw, data: { must_change_password: false } });
    setBusy(false);
    if (error) return setError(/different from the old/i.test(error.message) ? "Choose a password you haven't used before." : error.message);
    endSetPassword();
    const home = homePath();
    window.history.replaceState(null, "", home);
    nav(home, { replace: true });
  }

  const title = reason === "invite" ? "Welcome to Tracking Fiji" : reason === "reset" ? "Choose a new password" : "Change your temporary password";
  const intro = reason === "invite"
    ? "Set a password to finish creating your account."
    : reason === "temporary"
      ? "You signed in with a temporary password. Please choose your own before continuing."
      : "Enter a new password for your account.";

  return (
    <div className="login">
      <form className="login-card" onSubmit={submit}>
        <img src="/logo.png" alt="Tracking Fiji" className="login-logo" />
        <h1>{title}</h1>
        <p className="muted">{intro}</p>
        <p className="mono muted">{session.user.email}</p>
        <label>
          New password
          <input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={MIN} />
        </label>
        <label>
          Repeat new password
          <input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={MIN} />
        </label>
        <p className="fine" style={{ textAlign: "left" }}>At least {MIN} characters. A short phrase of 3–4 words is easy to remember and hard to guess.</p>
        {error && <div className="error">{error}</div>}
        <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save password"}</button>
        <button type="button" className="btn-ghost" onClick={() => { endSetPassword(); supabase.auth.signOut(); }}>Cancel and sign out</button>
      </form>
    </div>
  );
}
